import { useState, useCallback, useMemo, useEffect } from 'react'
import { Check, Loader2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  InfluencerData,
  InfluencerTableFilters,
} from '../../../core/interface/chronique.interface'
import InfluencerTableHeader from './header/InfluencerTableHeader'
import InfluencerTableView from './body/InfluencerTableView'
import InfluencerDetailsContainer from './details/InfluencerDetailsContainer'
import { Button } from '../../ui/button'
import { Sheet, SheetContent } from '../../ui/sheet'
import { SocialMediaPlatformEnum } from '../../../core/enums/media-platform.enum'
import {
  InfluencerForInfluencedBookStatusEnum,
  InfluencerStatsEnum,
} from '../../../core/enums/influencer-status.enum'
import { TableViewTypeInfluencerEnum } from '../../../core/enums/table-view-type-influencer.enum'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
  PaginationNext,
  PaginationPrevious,
} from '../../ui/pagination'
import {
  InfluencerFilterSelectionEnum,
  InfluencerFilterYesNoEnum,
} from '../../../core/enums/influencer-filter.enum'

export type SortByType = {
  field:
    | keyof InfluencerData['influencer']
    | 'affinity'
    | 'current_influencedbook_status'
  direction: 'asc' | 'desc'
}

export interface InfluencerTableViewContainerProps {
  viewType: TableViewTypeInfluencerEnum
  chroniqueurId?: string
  data: InfluencerData[]
  onValidate?: (selectedUsers: InfluencerData[]) => void
  selectedUsers: InfluencerData[]
  setSelectedUsers: (selectedUsers: InfluencerData[]) => void
  userSelected: InfluencerData | null
  setUserSelected: (user: InfluencerData | null) => void
  showFilters: boolean
  extraHeaderProps?: any
  extraTableProps?: any
  extraDetailsProps?: any
  headerFilters: InfluencerTableFilters
  setHeaderFilters: (filters: InfluencerTableFilters) => void
  loading?: boolean
}

const InfluencerTableViewContainer = ({
  viewType,
  chroniqueurId,
  data,
  onValidate,
  selectedUsers,
  setSelectedUsers,
  userSelected,
  setUserSelected,
  showFilters,
  extraHeaderProps,
  extraTableProps,
  extraDetailsProps,
  headerFilters,
  setHeaderFilters,
  loading = false,
}: InfluencerTableViewContainerProps) => {
  const { t } = useTranslation()
  const [influencersData, setInfluencersData] = useState<InfluencerData[]>(data)
  const [proximitySort, setProximitySort] = useState<'asc' | 'desc'>('desc')

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 1000

  useEffect(() => {
    setInfluencersData(data)
  }, [data])

  // Données pour les filtres basées sur les vraies données
  const filterData = useMemo(() => {
    const allSelection = Object.values(InfluencerFilterSelectionEnum)
    const allStatus = Object.values(InfluencerStatsEnum)
    const yesNo = Object.values(InfluencerFilterYesNoEnum)
    const allCurrentInfluencedBookStatus = Object.values(
      InfluencerForInfluencedBookStatusEnum,
    )
    const allPromises = Object.values(SocialMediaPlatformEnum)

    return {
      selection: allSelection,
      status: allStatus,
      alreadyInfluenced: yesNo,
      chronique: allCurrentInfluencedBookStatus,
      complete: yesNo,
      promise: allPromises,
    }
  }, [influencersData])

  // Filtrage et tri des données
  const filteredData = useMemo(() => {
    let result = [...influencersData]

    // Application des filtres de header
    if (headerFilters.selection && headerFilters.selection?.length > 0) {
      result = result.filter((user) => {
        const isSelected = selectedUsers.some(
          (selected) => selected.influencer.userid === user.influencer.userid,
        )
        return (headerFilters.selection ?? []).includes(
          isSelected
            ? InfluencerFilterSelectionEnum.SELECTED
            : InfluencerFilterSelectionEnum.UNSELECTED,
        )
      })
    }

    if (headerFilters.status && headerFilters.status.length > 0) {
      result = result.filter((user) =>
        (headerFilters.status ?? []).includes(user.influencer.status),
      )
    }

    if (
      headerFilters.alreadyInfluenced &&
      headerFilters.alreadyInfluenced.length > 0
    ) {
      result = result.filter((user) => {
        const status = user.already_influenced_for_publisher
          ? InfluencerFilterYesNoEnum.YES
          : InfluencerFilterYesNoEnum.NO
        return (headerFilters.alreadyInfluenced ?? []).includes(status)
      })
    }

    if (headerFilters.chronique && headerFilters.chronique.length > 0) {
      result = result.filter((user) => {
        const status =
          user.current_influencedbook_status ||
          InfluencerForInfluencedBookStatusEnum.UNSELECTED
        return (headerFilters.chronique ?? []).includes(status)
      })
    }

    if (headerFilters.complete && headerFilters.complete.length > 0) {
      result = result.filter((user) => {
        const status = user.form_completed
          ? InfluencerFilterYesNoEnum.YES
          : InfluencerFilterYesNoEnum.NO
        return (headerFilters.complete ?? []).includes(status)
      })
    }

    if (headerFilters.promise && headerFilters.promise.length > 0) {
      result = result.filter((user) => {
        const userPlatforms = user.promises.map(
          (promise) => promise.platform_obj.platform,
        )
        return (headerFilters.promise ?? []).some((platform) =>
          userPlatforms.includes(platform),
        )
      })
    }

    return result
  }, [influencersData, headerFilters, selectedUsers]) // Retiré influencersData des dépendances

  // Tri des données filtrées
  const sortedAndFilteredData = useMemo(() => {
    if (!proximitySort) return filteredData

    return [...filteredData].sort((a, b) => {
      const affinityA = a.affinity || 0
      const affinityB = b.affinity || 0

      if (proximitySort === 'asc') {
        return affinityA - affinityB
      }
      return affinityB - affinityA
    })
  }, [filteredData, proximitySort])

  // Calcul de la pagination basée sur les données filtrées et triées
  const totalPages = Math.ceil(sortedAndFilteredData.length / itemsPerPage)

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  // Données paginées finales
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return sortedAndFilteredData.slice(startIndex, endIndex)
  }, [sortedAndFilteredData, currentPage, itemsPerPage])

  // Reset de la page courante quand les filtres changent
  useEffect(() => {
    setCurrentPage(1)
  }, [headerFilters])

  const handleUserSelect = (user: InfluencerData, selected: boolean) => {
    if (selected) {
      const newSelectedUsers = [...selectedUsers, user]
      setSelectedUsers(newSelectedUsers)
    } else {
      const newSelectedUsers = selectedUsers.filter(
        (u) => u.influencer.userid !== user.influencer.userid,
      )
      setSelectedUsers(newSelectedUsers)
    }
  }

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        // Sélectionner uniquement les éléments de la page courante
        const newSelectedUsers = [...selectedUsers]
        paginatedData.forEach((user) => {
          if (
            !newSelectedUsers.some(
              (selected) =>
                selected.influencer.userid === user.influencer.userid,
            )
          ) {
            newSelectedUsers.push(user)
          }
        })
        setSelectedUsers(newSelectedUsers)
      } else {
        // Désélectionner uniquement les éléments de la page courante
        const currentPageUserIds = paginatedData.map(
          (user) => user.influencer.userid,
        )
        const newSelectedUsers = selectedUsers.filter(
          (user) => !currentPageUserIds.includes(user.influencer.userid),
        )
        setSelectedUsers(newSelectedUsers)
      }
    },
    [paginatedData, selectedUsers, setSelectedUsers],
  )

  const handleUserClick = useCallback((user: InfluencerData) => {
    setUserSelected(user)
  }, [])

  const handleUpdateUser = (updatedUser: InfluencerData) => {
    setUserSelected(updatedUser)
    setInfluencersData((prevData) =>
      prevData.map((u) =>
        u.influencer.userid === updatedUser.influencer.userid ? updatedUser : u,
      ),
    )
  }

  const getPaginationItems = () => {
    const totalNumbers = 7
    const siblingCount = 1

    if (totalPages <= totalNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages)

    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount
      const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1)
      return [...leftRange, '...', totalPages]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount
      const rightRange = Array.from(
        { length: rightItemCount },
        (_, i) => totalPages - rightItemCount + i + 1,
      )
      return [1, '...', ...rightRange]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i,
      )
      return [1, '...', ...middleRange, '...', totalPages]
    }
    return []
  }

  return (
    <div className="flex flex-col h-auto bg-gray-50 overflow-auto">
      {showFilters && (
        <InfluencerTableHeader
          viewType={viewType}
          filters={headerFilters}
          onFiltersChange={setHeaderFilters}
          filterData={filterData}
          {...extraHeaderProps}
        />
      )}
      <div className="flex-1 flex overflow-hidden">
        <InfluencerTableView
          viewType={viewType}
          data={paginatedData}
          selectedUsers={selectedUsers}
          onUserSelect={handleUserSelect}
          onSelectAll={handleSelectAll}
          onUserClick={handleUserClick}
          userSelected={userSelected}
          proximitySort={proximitySort}
          onProximitySortChange={setProximitySort}
          {...extraTableProps}
        />
        <Sheet
          open={userSelected !== null}
          onOpenChange={() => setUserSelected(null)}
        >
          <SheetContent
            className={
              viewType === TableViewTypeInfluencerEnum.NEW_CHRONIQUEUR_VIEW
                ? ''
                : 'mt-12'
            }
          >
            <InfluencerDetailsContainer
              viewType={viewType}
              chroniqueurId={chroniqueurId}
              user={userSelected}
              onClose={() => setUserSelected(null)}
              onUpdateUser={handleUpdateUser}
              {...extraDetailsProps}
            />
          </SheetContent>
        </Sheet>
      </div>
      {totalPages > 1 && (
        <Pagination className="mt-2">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => handlePageChange(currentPage - 1)}
              />
            </PaginationItem>
            {getPaginationItems().map((item) =>
              typeof item === 'number' ? (
                <PaginationItem key={`page-${item}`}>
                  <PaginationLink
                    isActive={currentPage === item}
                    onClick={() => handlePageChange(item)}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              ) : (
                <PaginationEllipsis key={`ellipsis-${item}`} />
              ),
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() => handlePageChange(currentPage + 1)}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
      <div className="absolute bottom-4 mx-auto left-0 right-0 flex justify-center gap-2  w-fit">
        {selectedUsers.length > 0 && (
          <Button
            onClick={() => onValidate?.(selectedUsers)}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2Icon className="animate-spin" />
                {t('chroniqueurView.loading')}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {t('chroniqueurView.validateSelection')}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

export default InfluencerTableViewContainer
